import React, { ReactElement, useEffect, useRef } from 'react'
import { Text } from '../../atoms'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import KeyboardControl from '../KeyboardControls/KeyboardControl'
import ControlHighlight from '../KeyboardControls/ControlHighlight'
import { HighlightEffect } from '../../../enums/KeyboardControls'

interface ResetPasswordLinkProps {
  id?: string
  className?: string
  onFocus?: () => void
  onClick: () => void
}

export enum FocusableElement {
  LinkResetPassword = 'link-reset-password',
}

const ResetPasswordLink: React.FC<ResetPasswordLinkProps> = ({
  id,
  className,
  onFocus,
  onClick,
  ...rest
}: ResetPasswordLinkProps): ReactElement => {
  const keyboardControls = useKeyboardControls()
  const elementId = id ?? FocusableElement.LinkResetPassword
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Handle focus event
  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    // Call the onFocus prop if provided
    if (onFocus != null) {
      onFocus()
    }
  }

  // This effect ensures that the onFocus handler is called when the button is focused
  useEffect(() => {
    const button = buttonRef.current
    if (button == null) return

    const focusHandler = () => {
      if (onFocus != null) {
        onFocus()
      }
    }

    button.addEventListener('focus', focusHandler)

    return () => {
      button.removeEventListener('focus', focusHandler)
    }
  }, [onFocus])

  return (
    <div className={`${className ?? ''} text-center pt-6`} data-testid='reset-password-wrapper' {...rest}>
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(elementId)}
        useSpan
        highlightEffect={HighlightEffect.LinkUnderline}
        data-testid={`highlight-${elementId}`}
      >
        <span>
          <KeyboardControl>
            <button
              ref={buttonRef}
              id={elementId}
              data-testid={elementId}
              onFocus={handleFocus}
              className='text-dopage-primary hover:text-dopage-primary underline text-[16px] bg-transparent border-0 p-0 cursor-pointer'
              onClick={onClick}
            >
              <Text
                type='span'
                text='Reset Password'
                size='BASE'
              />
            </button>

          </KeyboardControl>
        </span>
      </ControlHighlight>
    </div>
  )
}

export default ResetPasswordLink
